import React, { useState, useEffect } from "react";
import {
  Steps,
  Button,
  Input,
  Select,
  Upload,
  message,
  Form,
  Typography,
  Image,
  Spin,
  DatePicker,
} from "antd";
import {
  UploadOutlined,
  InboxOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  HomeOutlined,
  PlusOutlined,
  MinusCircleOutlined,
} from "@ant-design/icons";
import AdminDashboard from "../Layout/AdminDashboard";
import axios from "axios";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

const { Step } = Steps;
const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

function AuditReport() {
  const [currentStep, setCurrentStep] = useState(0);
  const [auditItems, setAuditItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fileLists, setFileLists] = useState({}); // per-question files
  const [fileList, setFileList] = useState([]); // FSSAI upload fileList (antd Upload expects array)
  const [previewUrl, setPreviewUrl] = useState(null); // image preview for FSSAI
  const [searchParams] = useSearchParams();
  const audit_id = searchParams.get("audit_id");
  const checklistId = searchParams.get("checklistId");
  const category = searchParams.get("category");
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const params = useParams();
  const [products, setProducts] = useState([""]);
  const [equipment, setEquipment] = useState([""]);
  const [auditService, setAuditService] = useState("");

  // Consolidated form data state
  const [allFormData, setAllFormData] = useState({
    fssaiData: {},
    questionResponses: {},
    suggestions: [],
  });

  const apiUrl = process.env.REACT_APP_API_URL;

  // === validation ===
  const validateFile = (file) => {
    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "application/pdf",
    ];
    const maxSize = 5 * 1024 * 1024; // 5 MB

    if (!allowedTypes.includes(file.type)) {
      message.error("Only JPG, JPEG, PNG and PDF files are allowed.");
      return false;
    }

    if (file.size > maxSize) {
      message.error("File size must be less than 5 MB.");
      return false;
    }

    return true;
  };

  // === FSSAI Upload handlers ===
  const handleBeforeUploadFSSAI = (file) => {
    const valid = validateFile(file);
    if (!valid) {
      return Upload.LIST_IGNORE;
    }
    return false; // prevent auto upload
  };

  const handleFileChange = ({ fileList: newFileList }) => {
    if (newFileList.length > 0) {
      const fileObj = newFileList[0]?.originFileObj || newFileList[0];
      if (!fileObj) {
        setFileList(newFileList);
        return;
      }

      if (validateFile(fileObj)) {
        if (fileObj.type === "application/pdf") {
          setPreviewUrl(null);
        } else {
          const reader = new FileReader();
          reader.onloadend = () => {
            setPreviewUrl(reader.result);
          };
          reader.readAsDataURL(fileObj);
        }
        setFileList(newFileList);
      } else {
        setFileList([]);
        setPreviewUrl(null);
      }
    } else {
      setFileList([]);
      setPreviewUrl(null);
    }
  };

  const handleRemove = () => {
    setFileList([]);
    setPreviewUrl(null);
  };

  // === Question upload handlers (per-question) ===
  const handleBeforeUpload = (file) => {
    const valid = validateFile(file);
    if (!valid) return Upload.LIST_IGNORE;
    return false; // prevent automatic upload
  };

  const handleChange = (questionId, info) => {
    const qid = String(questionId);

    const fileRecord = info.fileList && info.fileList[0];
    if (!fileRecord) {
      setFileLists((prev) => {
        const copy = { ...prev };
        delete copy[qid];
        return copy;
      });
      return;
    }

    const originFile = fileRecord.originFileObj || fileRecord;
    if (!originFile) {
      setFileLists((prev) => ({ ...prev, [qid]: [{ url: null, file: fileRecord }] }));
      return;
    }

    if (!validateFile(originFile)) {
      setFileLists((prev) => {
        const copy = { ...prev };
        delete copy[qid];
        return copy;
      });
      return;
    }

    if (originFile.type === "application/pdf") {
      setFileLists((prev) => ({
        ...prev,
        [qid]: [{ url: null, file: { ...fileRecord, originFileObj: originFile } }],
      }));
    } else {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFileLists((prev) => ({
          ...prev,
          [qid]: [{ url: reader.result, file: { ...fileRecord, originFileObj: originFile } }],
        }));
      };
      reader.readAsDataURL(originFile);
    }
  };

  const handleRemoveImage = (questionId) => {
    const qid = String(questionId);
    setFileLists((prev) => {
      const copy = { ...prev };
      delete copy[qid];
      return copy;
    });
  };

  // === fetch sections/questions ===
  useEffect(() => {
    const fetchSections = async () => {
      try {
        setLoading(true);

        const response = await axios.get(
          `${apiUrl}/api/auditor/fetchLabelsWithQuestions/${checklistId}`
        );

        const { categoryName, service, data } = response.data || {};
        setAuditService(service);
        console.log("Fetched sections data:", service, data);
        setAuditItems(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching sections:", error);
        message.error("Failed to fetch section data.");
      } finally {
        setLoading(false);
      }
    };

    if (checklistId) fetchSections();
  }, [checklistId, apiUrl]);

  // === navigation & form saving ===
  const next = () => {
    form
      .validateFields()
      .then((values) => {
        if (currentStep === 0) {
          // Save FSSAI data
          setAllFormData((prev) => ({
            ...prev,
            fssaiData: {
              ...values,
              inspectedProducts: products.filter(Boolean),
              equipmentUsed: equipment.filter(Boolean),
              fssaiFile: fileList[0]?.originFileObj || null,
            },
          }));
        } else {
          setAllFormData((prev) => ({
            ...prev,
            questionResponses: {
              ...prev.questionResponses,
              ...values.responses,
            },
            suggestions: values.suggestions || prev.suggestions || [],
          }));
        }

        setCurrentStep((s) => s + 1);
        // NOTE: do not reset fields here to preserve suggestions/responses across steps
        // form.resetFields();
      })
      .catch((errorInfo) => {
        console.log("Validation failed:", errorInfo);
      });
  };

  const prev = () => {
    setCurrentStep((s) => Math.max(0, s - 1));
  };

  const goBack = () => navigate(-1);

  const handleSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        const finalFormData = {
          ...allFormData,
          questionResponses: {
            ...allFormData.questionResponses,
            ...values.responses,
          },
          suggestions: values.suggestions || allFormData.suggestions || [],
        };

        setAllFormData(finalFormData);
        handleFormSubmit("draft", finalFormData);
      })
      .catch((errorInfo) => {
        console.log("Validation failed:", errorInfo);
      });
  };

  const handleFormSubmit = async (status, formDataToSubmit = allFormData) => {
    try {
      setLoading(true);
      const responses = [];

      Object.entries(formDataToSubmit.questionResponses || {}).forEach(
        ([questionId, questionData]) => {
          const fileInfo = fileLists[questionId]?.[0];
          const response = {
            questionId,
            comment: questionData.comment || "",
            marks: questionData.selectedMark || 0,
            file: fileInfo?.file ? fileInfo.file.name : "",
          };
          responses.push(response);
        }
      );

      const submissionFormData = new FormData();

      const dataPayload = {
        auditId: audit_id,
        responses,
        status,
        fssai_number: formDataToSubmit.fssaiData?.fssaiLicense || "",
        auditee_name: formDataToSubmit.fssaiData?.auditeeName || "",
        fostac_person: formDataToSubmit.fssaiData?.fostacPerson || "",
        fostac_certificate_number: formDataToSubmit.fssaiData?.fostacCertificate || "",
        fostac_certificate_validity: formDataToSubmit.fssaiData
          ?.fostacCertificateValidity
          ? formDataToSubmit.fssaiData.fostacCertificateValidity.format("YYYY-MM-DD")
          : null,
        fssai_file: formDataToSubmit.fssaiData?.fssaiFile?.name || "",
        inspectedProducts: formDataToSubmit.fssaiData?.inspectedProducts || [],
        equipmentUsed: formDataToSubmit.fssaiData?.equipmentUsed || [],
        suggestions: formDataToSubmit.suggestions || [], // <-- important: include suggestions
      };

      submissionFormData.append("data", JSON.stringify(dataPayload));

      // Append question files
      Object.entries(fileLists).forEach(([questionId, fileData]) => {
        if (fileData[0]?.file?.originFileObj) {
          submissionFormData.append(
            "files",
            fileData[0].file.originFileObj,
            fileData[0].file.name
          );
        }
      });

      // Append FSSAI file
      if (formDataToSubmit.fssaiData?.fssaiFile) {
        submissionFormData.append(
          "files",
          formDataToSubmit.fssaiData.fssaiFile,
          formDataToSubmit.fssaiData.fssaiFile.name
        );
      }
      console.log("Submitting form data:", dataPayload);
      console.log("Submitting :", submissionFormData);
      await axios.post(
        `${apiUrl}/api/auditor/saveAuditResponses`,
        submissionFormData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      message.success("Audit report submitted successfully!");
      navigate("/assigned-audit");
    } catch (error) {
      console.error("Error submitting responses:", error);
      message.error("Failed to submit responses. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // === dynamic arrays handlers ===
  const handleAdd = (list, setter) => setter([...list, ""]);
  const handleRemoveItem = (list, setter, idx) =>
    setter(list.filter((_, i) => i !== idx));
  const handleChangeItem = (list, setter, idx, value) => {
    const updated = [...list];
    updated[idx] = value;
    setter(updated);
  };

  // === UI renderers ===
  const renderFssaiUI = () => (
    <div>
      <div className="text-lg text-gray-800 bg-white border mt-5 p-8 rounded-xl">
        The FSSAI license/Registration and Food Safety Display Board (FSDB) both
        are displayed at a prominent location.
        <br />
        (Note: The FSDBs are readable to both Food Handlers and Customers.)
      </div>

      <Form layout="vertical" form={form} style={{ marginTop: 24 }}>
        <Form.Item
          label="FSSAI License Number"
          name="fssaiLicense"
          rules={[
            {
              required: true,
              message: "Please enter your 14-digit FSSAI License No.",
            },
            { pattern: /^\d{14}$/, message: "Must be exactly 14 digits" },
          ]}
        >
          <Input maxLength={14} />
        </Form.Item>
        {/* {auditService && auditService.toUpperCase() !== "TPA" && (
          <Form.Item
            label="FSSAI License Number"
            name="fssaiLicense"
            rules={[
              {
                required: true,
                message: "Please enter your 14-digit FSSAI License No.",
              },
              { pattern: /^\d{14}$/, message: "Must be exactly 14 digits" },
            ]}
          >
            <Input maxLength={14} />
          </Form.Item>
        )} */}

        <Form.Item
          label="Auditee Name"
          name="auditeeName"
          rules={[{ required: true, message: "Please enter your Auditee Name." }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Fostac Person"
          name="fostacPerson"
          rules={[{ required: true, message: "Please enter the Fostac Person Name." }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Fostac Certificate Number"
          name="fostacCertificate"
          rules={[
            {
              required: true,
              message: "Please enter the Fostac Certificate Number.",
            },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Fostac Certificate Validity"
          name="fostacCertificateValidity"
          rules={[{ required: true, message: "Please select the expiry date" }]}
        >
          <DatePicker format="DD/MM/YYYY" className="w-full" />
        </Form.Item>

        <Form.Item label="Inspected Product Names">
          {products.map((p, idx) => (
            <div key={idx} className="flex items-center gap-2 mb-2">
              <Input
                placeholder="Enter product name"
                value={p}
                onChange={(e) =>
                  handleChangeItem(products, setProducts, idx, e.target.value)
                }
              />
              {products.length > 1 && (
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  size="small"
                  onClick={() => handleRemoveItem(products, setProducts, idx)}
                />
              )}
            </div>
          ))}
          <Button type="dashed" onClick={() => handleAdd(products, setProducts)}>
            + Add Product
          </Button>
        </Form.Item>

        <Form.Item label="Equipment Used">
          {equipment.map((eq, idx) => (
            <div key={idx} className="flex items-center gap-2 mb-2">
              <Input
                placeholder="Enter equipment name"
                value={eq}
                onChange={(e) =>
                  handleChangeItem(equipment, setEquipment, idx, e.target.value)
                }
              />
              {equipment.length > 1 && (
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  size="small"
                  onClick={() => handleRemoveItem(equipment, setEquipment, idx)}
                />
              )}
            </div>
          ))}
          <Button type="dashed" onClick={() => handleAdd(equipment, setEquipment)}>
            + Add Equipment
          </Button>
        </Form.Item>

        {/* Image / PDF Upload Part in Audit Reports - FSSAI Certificate */}
        <div className="flex justify-center w-full mt-4">
          {fileList.length === 0 ? (
            <Form.Item name="mediaUpload" label="Upload FSSAI Certificate">
              <Upload.Dragger
                accept=".jpg,.png,.jpeg,.pdf"
                maxCount={1}
                beforeUpload={handleBeforeUploadFSSAI}
                onChange={handleFileChange}
                listType="text"
                fileList={fileList}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">Drag or click to browse</p>
                <p className="ant-upload-hint">Max 5 MB – .jpg, .png, .jpeg, .pdf only</p>
              </Upload.Dragger>
            </Form.Item>
          ) : (
            <div className="flex flex-col items-center">
              {/* Image preview */}
              {previewUrl ? (
                <Image
                  src={previewUrl}
                  alt="Preview"
                  width={200}
                  style={{ marginBottom: 16, borderRadius: 8 }}
                  preview={{ mask: "Click to preview" }}
                />
              ) : (
                fileList[0]?.originFileObj?.type === "application/pdf" && (
                  <a
                    href={URL.createObjectURL(fileList[0].originFileObj)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline mb-4"
                  >
                    View PDF
                  </a>
                )
              )}

              <Button type="primary" danger icon={<DeleteOutlined />} onClick={handleRemove}>
                Remove
              </Button>
            </div>
          )}
        </div>

        <Text type="secondary" className="block text-center mt-4">
          Note: All the details above are mandatory to proceed to the next section.
        </Text>
      </Form>
    </div>
  );
  useEffect(() => {
    if (currentStep > 0) {
      const currentSection = auditItems[currentStep - 1];

      if (!currentSection) return;

      const responses = {};

      currentSection.questions.forEach((question) => {
        const qid = String(question.questionId);

        responses[qid] =
          allFormData.questionResponses?.[qid] || {
            comment: question.prefillAnswer || "",
            selectedMark: undefined,
          };
      });

      form.setFieldsValue({
        responses,
      });
    }
  }, [currentStep, auditItems]);

  // Questions UI renderer with aligned question number + upload/preview handling
  const renderQuestionsUI = () => {
    const currentSection = auditItems[currentStep - 1];
    if (!currentSection) return null;

    const initialResponses =
      (currentSection.questions || []).reduce((acc, question) => {
        const qid = String(question.questionId);
        acc[qid] = {
          comment: question.prefillAnswer || "",
          selectedMark: undefined,
        };
        return acc;
      }, {}) || {};

    // Use any existing suggestions from state as initial values
    const initialSuggestions = allFormData.suggestions || [];

    return (
      <div>
        <Form
          layout="vertical"
          form={form}
          initialValues={{ responses: initialResponses, suggestions: initialSuggestions }}
        >
          {currentSection.questions
            ?.sort((a, b) => a.position - b.position)
            .map((question, questionIndex) => {
              const qid = String(question.questionId);
              const uploaded = fileLists[qid] && fileLists[qid].length > 0;
              const fileEntry = uploaded ? fileLists[qid][0] : null;
              const fileUid = fileEntry?.file?.uid || `upload-${qid}`;

              return (
                <div
                  key={qid}
                  className="mt-8 p-6 rounded-lg shadow-lg bg-white "
                  style={{ alignItems: "flex-start" }}
                >
                  <div style={{ width: "10%", minWidth: 90 }} className="text-center">
                    <div className="border rounded-lg p-2">
                      <div className="mt-1 text-sm text-gray-600">Mark: {question.mark}</div>
                    </div>
                  </div>

                  <div style={{ flex: 1 }}>
                    <p className="text-gray-800 text-lg p-2">{question.position} {question.description}</p>

                    <div className="mt-4 flex items-center justify-around">
                      <Form.Item name={["responses", qid, "comment"]} className="mt-2 w-1/2">
                        <TextArea rows={3} placeholder="Observation..." />
                      </Form.Item>

                      <div>
                        <Form.Item name={["responses", qid, "selectedMark"]}>
                          <Select placeholder="Select a mark" style={{ width: 120 }}>
                            <Option key="N/A" value="N/A">
                              N/A
                            </Option>
                            {question.mark === 2
                              ? Array.from({ length: 3 }, (_, idx) => (
                                <Option key={idx} value={idx}>
                                  {idx}
                                </Option>
                              ))
                              : question.mark === 4
                                ? [0, 4].map((value) => (
                                  <Option key={value} value={value}>
                                    {value}
                                  </Option>
                                ))
                                : null}
                          </Select>
                        </Form.Item>
                      </div>

                      {/* Upload / Preview area */}
                      <div style={{ minWidth: 140, marginBottom: "24px" }}>
                        {!uploaded ? (
                          <Upload
                            key={fileUid}
                            beforeUpload={handleBeforeUpload}
                            onChange={(info) => handleChange(qid, info)}
                            showUploadList={false}
                            accept=".jpg,.png,.jpeg,.pdf"
                          >
                            <Button icon={<UploadOutlined />}>Upload</Button>
                          </Upload>
                        ) : (
                          <div className="flex flex-col items-center">
                            {fileEntry?.url ? (
                              <Image
                                src={fileEntry.url}
                                alt="Preview"
                                width={100}
                                height={80}
                                style={{ objectFit: "cover", borderRadius: 6 }}
                                preview={{ mask: "Click to preview" }}
                              />
                            ) : fileEntry?.file?.originFileObj?.type === "application/pdf" ? (
                              <a
                                href={URL.createObjectURL(fileEntry.file.originFileObj)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 underline mb-2"
                              >
                                View PDF
                              </a>
                            ) : null}

                            <Button
                              danger
                              className="mt-2"
                              icon={<DeleteOutlined />}
                              onClick={() => handleRemoveImage(qid)}
                            >
                              Remove
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

          {/* ---------- SUGGESTIONS SECTION (CONDITIONALLY RENDERED) ---------- */}
          {auditService?.toUpperCase() === "TPA" && currentStep === auditItems.length && (
            <div className="mt-6 p-4 bg-white rounded-md shadow-sm">
              <h3 className="mb-3 text-base font-medium">Suggestions</h3>

              <Form.List name="suggestions">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map((field) => (
                      <div key={field.key} className="flex items-start gap-2 mb-3">
                        <Form.Item
                          {...field}
                          name={[field.name]}
                          fieldKey={[field.fieldKey]}
                          rules={[{ required: true, message: "Please enter suggestion" }]}
                          style={{ flex: 1 }}
                        >
                          <Input.TextArea rows={2} placeholder="Add suggestion or improvement..." />
                        </Form.Item>
                        <Button
                          type="text"
                          icon={<MinusCircleOutlined />}
                          onClick={() => remove(field.name)}
                        />
                      </div>
                    ))}

                    <Form.Item>
                      <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                        Add Suggestion
                      </Button>
                    </Form.Item>
                  </>
                )}
              </Form.List>

              {/* <Text type="secondary">Add suggestions or improvements you recommend for this section.</Text> */}
            </div>
          )}
        </Form>
      </div>
    );
  };

  return (
    <AdminDashboard>
      <div className="p-8">
        <div className="flex justify-center mb-5">
          <h1 className="text-xl font-medium">
            Audit Reports <span>({category})</span>
          </h1>
        </div>

        <Steps current={currentStep}>
          <Step title="FSSAI License" />
          {auditItems.map((item, index) => (
            <Step key={index} title={item.title} />
          ))}
        </Steps>

        {loading ? (
          <div className="flex justify-center mt-8">
            <Spin size="medium" />
          </div>
        ) : (
          <div className="flex justify-between mt-8">
            <h2 className="text-lg text-gray-600">
              {currentStep === 0 ? "FSSAI License" : auditItems[currentStep - 1]?.title}
            </h2>
            <div>
              <Button
                type="link"
                icon={<ArrowLeftOutlined />}
                onClick={prev}
                disabled={currentStep === 0}
              >
                Back
              </Button>

              <Button type="link" onClick={goBack} icon={<HomeOutlined />} />

              {currentStep < auditItems.length && (
                <Button type="link" icon={<ArrowRightOutlined />} onClick={next}>
                  Next
                </Button>
              )}

              {currentStep === auditItems.length && (
                <Button type="primary" onClick={handleSubmit}>
                  Submit
                </Button>
              )}
            </div>
          </div>
        )}

        {currentStep === 0 ? renderFssaiUI() : renderQuestionsUI()}
      </div>
    </AdminDashboard>
  );
}

export default AuditReport;
